import { AntennasGame, Antenna, Signal, BadgeInfo, AntennaColor } from './AntennasGame.js';
import { AntennasVortex } from './AntennasVortex.js';
import { Grid } from '../../core/Grid.js';
import { Position, Direction, positionKey, parsePositionKey } from '../../core/GridGeometry.js';
import { GridRenderer } from '../../rendering/GridRenderer.js';
import { RenderConfig, COLORS } from '../../rendering/Renderer.js';
import { isVortexGame } from '../../games/IVortexGame.js';
import {
    TweenScheduler, MOTION, turnDelta, PieceSet, Ranked, pruneMemo
} from '../../rendering/juice.js';

// Direction deltas for rendering
const DIRECTION_DELTAS: Record<Direction, { dr: number; dc: number }> = {
    'N': { dr: -1, dc: 0 },
    'E': { dr: 0, dc: 1 },
    'S': { dr: 1, dc: 0 },
    'W': { dr: 0, dc: -1 }
};

export interface AntennasRenderConfig extends Partial<RenderConfig> {
    antennaColor?: string;
    antennaWidth?: number;
    signalColor?: string;
    signalWidth?: number;
    buildingColor?: string;
    endpointRadius?: number;
}

const ANTENNAS_DEFAULTS = {
    antennaColor: COLORS.teal,
    antennaWidth: 6,
    signalColor: COLORS.red,
    signalWidth: 2,
    buildingColor: COLORS.blue,
    endpointRadius: 16  // Doubled from 8
};

// ── Antenna animation (docs/juice.md) ─────────────────────────────────
//
// An antenna is built receiver → emitter, one cell at a time, so it is drawn
// that way: the body is a run of links, each stroked on from its receiver-side
// end. That is the piece's own direction — no gesture is read here, because the
// antenna already knows which way round it goes.
//
// Everything is keyed by *where it sits* — a link by its two cells, a receiver
// or an emitter by its cell. A build that becomes a finished antenna, or a
// finished antenna reopened for editing, changes colour but not geometry, so it
// keeps the same keys and nothing animates: the handover is free.
//
// Signals are simulation and are never animated — they, and the badges, arrows
// and warnings they drive, re-solve instantly.

// An antenna arrives receiver first and leaves emitter first, so every piece
// carries both ranks (`Ranked`) and they are mirrors of each other.

interface LinkInfo extends Ranked {
    /** The receiver-side end — the end a link is drawn on from. */
    from: Position;
    to: Position;
    color: string;
}

/** A receiver, or the disc anchoring a build in progress. */
interface DiscInfo extends Ranked {
    color: string;
    /** The white centre dot, which a finished receiver has and a pending one doesn't. */
    dot: boolean;
}

interface EmitterInfo extends Ranked {
    dir: Direction;
    color: string;
}

/** A turn of an emitter, from wherever it was when the player cycled it. */
interface EmitterSpin { from: number; to: number; start: number; }

export class AntennasRenderer extends GridRenderer {
    private antennaConfig: typeof ANTENNAS_DEFAULTS;
    private gateMode: boolean = false;

    // Animation state, all renderer-local (docs/juice.md)
    private tweens = new TweenScheduler();
    private links = new PieceSet(this.tweens);
    private discs = new PieceSet(this.tweens);
    private emitters = new PieceSet(this.tweens);
    /** What each piece looked like on its last real frame — read for the ones that
     *  are on their way out, since those are already gone from game state. */
    private linkMemo = new Map<string, LinkInfo>();
    private discMemo = new Map<string, DiscInfo>();
    private emitterMemo = new Map<string, EmitterInfo>();
    private linkFrame = new Map<string, number>();
    private discFrame = new Map<string, number>();
    private emitterFrame = new Map<string, number>();
    /** Emitter headings, accumulated rather than renormalised, so repeated cycles
     *  keep winding the same way. */
    private emitterAngle = new Map<string, number>();
    private emitterSpin = new Map<string, EmitterSpin>();
    /** Set by any tween still running this frame; drives the self-scheduled rAF. */
    private animating: boolean = false;
    private lastGame: AntennasGame | null = null;

    constructor(canvas: HTMLCanvasElement, grid: Grid, config: AntennasRenderConfig = {}) {
        super(canvas, grid, config);
        this.antennaConfig = { ...ANTENNAS_DEFAULTS, ...config };
    }

    setGateMode(enabled: boolean): void {
        this.gateMode = enabled;
    }

    /** Disable tweening (see `TweenScheduler`). Drops anything in flight, so the
     *  next render draws every antenna at rest. */
    setSelfAnimation(enabled: boolean): void {
        this.tweens.setEnabled(enabled);
        if (enabled) return;
        this.links.clear();
        this.discs.clear();
        this.emitters.clear();
        this.emitterSpin.clear();
    }

    private color(cat: AntennaColor): string {
        return cat === 'teal' ? COLORS.teal : cat === 'red' ? COLORS.red : COLORS.amber;
    }

    render(game: AntennasGame): void {
        const grid = game.getGrid();

        this.lastGame = game;
        this.animating = false;
        this.syncPieces(game);

        this.clear();
        this.drawGrid();

        if (this.gateMode && isVortexGame(game)) {
            this.drawGateModeHighlights(game);
        }

        this.drawSignals(game);
        this.drawAntennas(game);
        this.drawCurrentPath(game);
        this.drawDepartingPieces();

        // Draw double-crossed digit warnings + signal collision warnings
        this.drawDoubleCrossedWarnings(game);
        this.drawCollisionWarnings(game);
        this.drawSignalArrows(game);

        if (isVortexGame(game)) {
            this.drawActiveVortexGates(game);
            this.drawVortexGateDashedLines(game);
        }

        this.drawDigits(grid);
        this.drawKeyboardCursor();

        if (this.animating) {
            this.tweens.request(() => {
                if (this.lastGame) this.render(this.lastGame);
            });
        }
    }

    // ── Antenna animation (docs/juice.md) ─────────────────────────────

    /** Diff all three families against the last frame, then work out where every
     *  moving piece sits this one. */
    private syncPieces(game: AntennasGame): void {
        // Where each antenna sits in the chain, worked out once: it reads the
        // emitter badges, which are not cheap, and all three families want it.
        const chain = this.chainOrder(game);
        const links = this.collectLinks(game, chain);
        const discs = this.collectDiscs(game, chain);
        const emitters = this.collectEmitters(game, chain);

        const ranked = <T extends Ranked>(next: Map<string, T>, memo: Map<string, T>) =>
            (key: string, entering: boolean) => entering
                ? next.get(key)?.enterRank ?? 0
                : memo.get(key)?.exitRank ?? 0;

        // The memos still hold the last real frame here, which is what the pieces on
        // their way out are ranked and drawn by; they are refreshed below.
        this.links.sync(new Set(links.keys()), ranked(links, this.linkMemo));
        this.discs.sync(new Set(discs.keys()), ranked(discs, this.discMemo));
        const newEmitters = this.emitters.sync(new Set(emitters.keys()), ranked(emitters, this.emitterMemo));

        for (const [key, info] of links) this.linkMemo.set(key, info);
        for (const [key, info] of discs) this.discMemo.set(key, info);
        for (const [key, info] of emitters) this.emitterMemo.set(key, info);

        this.linkFrame = this.links.frames();
        this.discFrame = this.discs.frames();
        this.emitterFrame = this.emitters.frames();
        this.animating = this.linkFrame.size > 0 || this.discFrame.size > 0
            || this.emitterFrame.size > 0;

        this.syncEmitterSpins(emitters, newEmitters);

        pruneMemo(this.linkMemo, this.links.state, this.linkFrame);
        pruneMemo(this.discMemo, this.discs.state, this.discFrame);
        pruneMemo(this.emitterMemo, this.emitters.state, this.emitterFrame);
        pruneMemo(this.emitterAngle, this.emitters.state, this.emitterFrame);
        pruneMemo(this.emitterSpin, this.emitters.state, this.emitterFrame);
    }

    /** Order the antennas the way the game reads: along the chain of digits they
     *  carry, falling back to reading order for one not yet carrying a digit. */
    private chainOrder(game: AntennasGame): Map<number, number> {
        const ranked = [...game.getAntennas()].map(([id, antenna]) => {
            const badge = game.getEmitterBadge(antenna);
            const first = game.getFirstEnd(antenna);
            return {
                id,
                key: badge ? badge.digit : 100 + first.row * this.grid.size + first.col
            };
        });
        ranked.sort((x, y) => x.key - y.key);
        return new Map(ranked.map((entry, i) => [entry.id, i]));
    }

    /** A body link, keyed by the pair of cells it joins whichever way round. */
    private linkKey(a: Position, b: Position): string {
        return (a.row < b.row || (a.row === b.row && a.col < b.col))
            ? `${a.row},${a.col},${b.row},${b.col}`
            : `${b.row},${b.col},${a.row},${a.col}`;
    }

    private collectLinks(game: AntennasGame, chain: Map<number, number>): Map<string, LinkInfo> {
        const links = new Map<string, LinkInfo>();

        const addRun = (path: Position[], color: string, seat: number) => {
            for (let i = 1; i < path.length; i++) {
                links.set(this.linkKey(path[i - 1], path[i]), {
                    from: path[i - 1], to: path[i], color,
                    enterRank: seat * 100 + i,
                    exitRank: seat * 100 + (path.length - i)
                });
            }
        };

        for (const [id, antenna] of game.getAntennas()) {
            addRun(antenna.path, this.color(game.getBodyColor(antenna)), chain.get(id) ?? 0);
        }
        // The build in progress sits ahead of the finished ones: it is the newest
        // thing on the board and the only one the player is holding.
        addRun(game.getCurrentPath(), this.antennaConfig.buildingColor, -1);
        return links;
    }

    private collectDiscs(game: AntennasGame, chain: Map<number, number>): Map<string, DiscInfo> {
        const discs = new Map<string, DiscInfo>();

        for (const [id, antenna] of game.getAntennas()) {
            const seat = (chain.get(id) ?? 0) * 100;
            discs.set(positionKey(antenna.path[0]), {
                color: this.color(game.getReceiverColor(antenna)), dot: true,
                enterRank: seat, exitRank: seat + antenna.path.length
            });
        }

        const path = game.getCurrentPath();
        if (path.length > 0) {
            discs.set(positionKey(path[0]), {
                color: this.antennaConfig.buildingColor, dot: false,
                enterRank: -100, exitRank: -100 + path.length
            });
        }
        return discs;
    }

    private collectEmitters(game: AntennasGame, chain: Map<number, number>): Map<string, EmitterInfo> {
        const emitters = new Map<string, EmitterInfo>();

        for (const [id, antenna] of game.getAntennas()) {
            const seat = (chain.get(id) ?? 0) * 100;
            emitters.set(positionKey(this.getSignalEndOf(antenna)), {
                dir: antenna.signalDirection,
                color: this.color(game.getEmitterColor(antenna)),
                enterRank: seat + antenna.path.length, exitRank: seat
            });
        }
        return emitters;
    }

    private getSignalEndOf(antenna: Antenna): Position {
        return antenna.path[antenna.path.length - 1];
    }

    /**
     * Cycling an emitter turns it to its next heading (`change`), the short way
     * round; a half-turn goes clockwise, matching the N → E → S → W order the cycle
     * itself runs in. A newly placed emitter opens on its heading rather than
     * turning to it — it has no previous heading to have come from.
     */
    private syncEmitterSpins(emitters: Map<string, EmitterInfo>, arrived: Set<string>): void {
        const now = this.tweens.now();
        for (const [key, info] of emitters) {
            const target = this.dirAngle(info.dir);
            const rest = this.emitterAngle.get(key);
            if (rest === undefined || arrived.has(key) || !this.tweens.isEnabled()) {
                this.emitterAngle.set(key, target);
                this.emitterSpin.delete(key);
                continue;
            }
            const delta = turnDelta(rest, target, 1);
            if (Math.abs(delta) < 1e-6) continue;
            // Retarget from where the arrow actually is, so a click landing mid-turn
            // bends the motion instead of restarting it.
            const from = this.emitterAngleAt(key, now);
            this.emitterAngle.set(key, rest + delta);
            this.emitterSpin.set(key, { from, to: rest + delta, start: now });
        }
    }

    /** An emitter's heading this frame, mid-turn or at rest. */
    private emitterAngleAt(key: string, now: number = this.tweens.now()): number {
        const rest = this.emitterAngle.get(key) ?? 0;
        const spin = this.emitterSpin.get(key);
        if (!spin) return rest;

        const { value, done } = this.tweens.tween(spin.start, MOTION.change, now);
        if (done) {
            this.emitterSpin.delete(key);
            return rest;
        }
        this.animating = true;
        return spin.from + (spin.to - spin.from) * value;
    }

    private dirAngle(dir: Direction): number {
        switch (dir) {
            case 'E': return 0;
            case 'S': return Math.PI / 2;
            case 'W': return Math.PI;
            case 'N': return -Math.PI / 2;
        }
    }

    /** Pieces that are already gone from game state and exist only to leave. */
    private drawDepartingPieces(): void {
        const { antennaWidth } = this.antennaConfig;
        this.ctx.lineWidth = antennaWidth;
        this.ctx.lineCap = 'round';
        this.ctx.lineJoin = 'round';

        for (const [key, grown] of this.linkFrame) {
            if (this.links.state.has(key)) continue;
            const info = this.linkMemo.get(key);
            if (!info) continue;
            this.ctx.strokeStyle = info.color;
            this.strokeLink(info.from, info.to, grown);
        }
        for (const [key, grown] of this.discFrame) {
            if (this.discs.state.has(key)) continue;
            const info = this.discMemo.get(key);
            if (!info) continue;
            this.drawReceiver(parsePositionKey(key), info.color, info.dot, grown);
        }
        for (const [key, grown] of this.emitterFrame) {
            if (this.emitters.state.has(key)) continue;
            const info = this.emitterMemo.get(key);
            if (!info) continue;
            this.drawEmitter(parsePositionKey(key), info.color, this.emitterAngleAt(key), grown);
        }
    }

    /** Scoring overlay: redraw the antennas (primary) in amber. */
    protected drawScoringPrimary(game: AntennasGame): void {
        this.drawAntennas(game, COLORS.amber);
    }

    private drawAntennas(game: AntennasGame, colorOverride?: string): void {
        // The scoring overlay redraws the antennas in a flat colour on a frozen
        // board: it reads state, it doesn't advance it. (The diff already ran, in
        // render.) Each antenna is drawn whole before the next one starts, so a
        // badge is never covered by a neighbour that happens to be drawn later.
        const live = colorOverride === undefined;

        for (const [, antenna] of game.getAntennas()) {
            this.drawAntenna(
                antenna,
                colorOverride ?? this.color(game.getBodyColor(antenna)),
                colorOverride ?? this.color(game.getEmitterColor(antenna)),
                colorOverride ?? this.color(game.getReceiverColor(antenna)),
                live
            );
            if (!live) continue;

            const emitterBadge = game.getEmitterBadge(antenna);
            if (emitterBadge) this.drawEndBadge(game.getSignalEnd(antenna), emitterBadge);

            const receiverBadge = game.getReceiverBadge(antenna);
            if (receiverBadge) this.drawEndBadge(game.getFirstEnd(antenna), receiverBadge);
        }
    }

    private drawEndBadge(pos: Position, badge: BadgeInfo): void {
        this.drawBadge(pos, 'NE', badge.digit.toString(), this.color(badge.color));
    }

    /**
     * The emitter: an isoceles triangle with its base at the cell centre and its tip
     * pointing along `angle`, plus a white notch matching the receiver's dot. It
     * grows tip-first out of its base — the way it throws its signal — so `grown`
     * scales the height and leaves the base where it is. At `grown: 0` the triangle
     * is degenerate and covers nothing, which is exactly right.
     */
    private drawEmitter(cell: Position, color: string, angle: number, grown: number): void {
        if (grown <= 0) return;
        const size = this.antennaConfig.endpointRadius;
        const centre = this.getCellCenter(cell);

        const halfBase = size * 1;
        const height = size * 1.4 * grown;
        const baseOffset = size * 0.2;   // the base sits back off the antenna's end
        const notchDepth = size * 1.4 * 0.5 * grown;
        const notchHalfBase = halfBase * 0.5;

        this.ctx.save();
        this.ctx.translate(centre.x, centre.y);
        this.ctx.rotate(angle);

        this.ctx.fillStyle = color;
        this.ctx.beginPath();
        this.ctx.moveTo(height - baseOffset, 0);
        this.ctx.lineTo(-baseOffset, -halfBase);
        this.ctx.lineTo(-baseOffset, halfBase);
        this.ctx.closePath();
        this.ctx.fill();

        this.ctx.fillStyle = COLORS.white;
        this.ctx.beginPath();
        this.ctx.moveTo(notchDepth - baseOffset, 0);
        this.ctx.lineTo(-baseOffset, -notchHalfBase);
        this.ctx.lineTo(-baseOffset, notchHalfBase);
        this.ctx.closePath();
        this.ctx.fill();

        this.ctx.restore();
    }

    /** A receiver, or the disc anchoring a build, at `grown` of its full size. */
    private drawReceiver(cell: Position, color: string, dot: boolean, grown: number): void {
        if (grown <= 0) return;
        const { endpointRadius } = this.antennaConfig;
        const centre = this.getCellCenter(cell);

        this.ctx.fillStyle = color;
        this.ctx.beginPath();
        this.ctx.arc(centre.x, centre.y, endpointRadius * grown, 0, Math.PI * 2);
        this.ctx.fill();

        if (!dot) return;
        this.ctx.fillStyle = COLORS.white;
        this.ctx.beginPath();
        this.ctx.arc(centre.x, centre.y, endpointRadius * 0.4 * grown, 0, Math.PI * 2);
        this.ctx.fill();
    }

    /** The `grown` fraction of one body link, drawn on from its receiver-side end. */
    private strokeLink(from: Position, to: Position, grown: number): void {
        if (grown <= 0) return;
        const a = this.getCellCenter(from);
        const b = this.getCellCenter(to);
        this.ctx.beginPath();
        this.ctx.moveTo(a.x, a.y);
        this.ctx.lineTo(a.x + (b.x - a.x) * grown, a.y + (b.y - a.y) * grown);
        this.ctx.stroke();
    }

    /**
     * A body, link by link. Each is a separate stroke, which at rest draws exactly
     * what one round-jointed polyline did — a round cap at a shared cell centre is
     * the same mark as a round join.
     */
    private drawBody(path: Position[], color: string, live: boolean): void {
        const { antennaWidth } = this.antennaConfig;
        this.ctx.strokeStyle = color;
        this.ctx.lineWidth = antennaWidth;
        this.ctx.lineCap = 'round';
        this.ctx.lineJoin = 'round';

        for (let i = 1; i < path.length; i++) {
            const grown = live
                ? this.linkFrame.get(this.linkKey(path[i - 1], path[i])) ?? 1
                : 1;
            this.strokeLink(path[i - 1], path[i], grown);
        }
    }

    private drawAntenna(
        antenna: Antenna, color: string, emitterColor: string, receiverColor: string, live: boolean
    ): void {
        if (antenna.path.length === 0) return;
        const receiver = antenna.path[0];
        const emitter = this.getSignalEndOf(antenna);

        this.drawBody(antenna.path, color, live);
        this.drawReceiver(receiver, receiverColor, true,
            live ? this.discFrame.get(positionKey(receiver)) ?? 1 : 1);

        const key = positionKey(emitter);
        this.drawEmitter(
            emitter, emitterColor,
            live ? this.emitterAngleAt(key) : this.dirAngle(antenna.signalDirection),
            live ? this.emitterFrame.get(key) ?? 1 : 1
        );
    }

    private drawCurrentPath(game: AntennasGame): void {
        const path = game.getCurrentPath();
        if (path.length === 0) return;

        const { buildingColor, endpointRadius } = this.antennaConfig;

        this.drawBody(path, buildingColor, true);
        this.drawReceiver(path[0], buildingColor, false,
            this.discFrame.get(positionKey(path[0])) ?? 1);

        // The small end disc is the pen: it rides the head of the link being drawn
        // on, so the build visibly reaches the cell the player just claimed.
        const tip = this.buildTip(path);
        this.ctx.fillStyle = buildingColor;
        this.ctx.beginPath();
        this.ctx.arc(tip.x, tip.y, endpointRadius * 0.6, 0, Math.PI * 2);
        this.ctx.fill();
    }

    /** Where the build currently ends on screen — the head of its last link while
     *  that link is still being drawn on, its end cell once it has landed. */
    private buildTip(path: Position[]): { x: number; y: number } {
        const end = this.getCellCenter(path[path.length - 1]);
        if (path.length < 2) return end;

        const grown = this.linkFrame.get(this.linkKey(path[path.length - 2], path[path.length - 1]));
        if (grown === undefined) return end;

        const prev = this.getCellCenter(path[path.length - 2]);
        return { x: prev.x + (end.x - prev.x) * grown, y: prev.y + (end.y - prev.y) * grown };
    }

    private drawSignals(game: AntennasGame): void {
        for (const [, antenna] of game.getAntennas()) {
            const signal = game.getSignalForAntenna(antenna);
            this.drawSignal(game, antenna, signal, this.color(game.getSignalColor(antenna)));
        }
    }

    private drawSignal(
        game: AntennasGame, antenna: Antenna, signal: Signal, color: string
    ): void {
        this.ctx.strokeStyle = color;
        this.ctx.lineWidth = this.antennaConfig.signalWidth;
        this.ctx.setLineDash([]);
        this.ctx.lineCap = 'round';

        const segments = this.clipSegmentsAtDigits(this.buildSignalSegments(game, antenna, signal));
        for (const seg of segments) {
            this.ctx.beginPath();
            this.ctx.moveTo(seg.from.x, seg.from.y);
            this.ctx.lineTo(seg.to.x, seg.to.y);
            this.ctx.stroke();
        }
    }

    private buildSignalSegments(
        game: AntennasGame,
        antenna: Antenna,
        signal: Signal
    ): Array<{ from: { x: number; y: number }; to: { x: number; y: number } }> {
        const segments: Array<{ from: { x: number; y: number }; to: { x: number; y: number } }> = [];
        const delta = DIRECTION_DELTAS[signal.direction];
        const { padding, cellSize } = this.config;
        const gridPixelSize = cellSize * this.grid.size;
        const overhang = 15;

        const getExitEdge = (pos: Position) => {
            let x = padding + (pos.col + 0.5) * cellSize;
            let y = padding + (pos.row + 0.5) * cellSize;
            if (delta.dr < 0) y = padding - overhang;
            if (delta.dr > 0) y = padding + gridPixelSize + overhang;
            if (delta.dc < 0) x = padding - overhang;
            if (delta.dc > 0) x = padding + gridPixelSize + overhang;
            return { x, y };
        };

        const getEntryEdge = (pos: Position) => {
            let x = padding + (pos.col + 0.5) * cellSize;
            let y = padding + (pos.row + 0.5) * cellSize;
            if (delta.dr < 0) y = padding + gridPixelSize + overhang;
            if (delta.dr > 0) y = padding - overhang;
            if (delta.dc < 0) x = padding + gridPixelSize + overhang;
            if (delta.dc > 0) x = padding - overhang;
            return { x, y };
        };

        const getGatePair = (pos: Position): Position | null => {
            if (!(game instanceof AntennasVortex)) return null;
            const key = positionKey(pos);
            const pairedKey = game.getActiveGatePairs().get(key);
            if (pairedKey) return parsePositionKey(pairedKey);
            return null;
        };

        const hitsAntenna = (pos: Position): boolean => {
            for (const [, other] of game.getAntennas()) {
                if (other.id === antenna.id) continue;
                for (const cell of other.path) {
                    if (pos.row === cell.row && pos.col === cell.col) return true;
                }
            }
            return false;
        };

        let segStart = this.getCellCenter(signal.from);
        let current = { ...signal.from };
        const visited = new Set<string>();

        for (let safety = 0; safety < this.grid.size * this.grid.size; safety++) {
            const nextRow = current.row + delta.dr;
            const nextCol = current.col + delta.dc;
            const wraps = nextRow < 0 || nextRow >= this.grid.size ||
                          nextCol < 0 || nextCol >= this.grid.size;
            const nextPos = {
                row: (nextRow + this.grid.size) % this.grid.size,
                col: (nextCol + this.grid.size) % this.grid.size
            };

            const key = positionKey(nextPos);
            if (visited.has(key)) break;
            visited.add(key);

            const hit = hitsAntenna(nextPos);
            const teleport = hit ? null : getGatePair(nextPos);

            if (wraps) {
                segments.push({ from: segStart, to: getExitEdge(current) });
                const entry = getEntryEdge(nextPos);

                if (hit) {
                    segments.push({ from: entry, to: this.getCellCenter(nextPos) });
                    return segments;
                }

                if (teleport) {
                    segments.push({ from: entry, to: this.getCellCenter(nextPos) });
                    segStart = this.getCellCenter(teleport);
                    current = { ...teleport };
                    continue;
                }

                segStart = entry;
                current = nextPos;
                continue;
            }

            if (hit) {
                segments.push({ from: segStart, to: this.getCellCenter(nextPos) });
                return segments;
            }

            if (teleport) {
                segments.push({ from: segStart, to: this.getCellCenter(nextPos) });
                segStart = this.getCellCenter(teleport);
                current = { ...teleport };
                continue;
            }

            current = nextPos;
        }

        // Signal didn't hit anything — draw to edge
        segments.push({ from: segStart, to: getExitEdge(current) });
        return segments;
    }

    private drawDoubleCrossedWarnings(game: AntennasGame): void {
        const doubleCrossed = game.getDoubleCrossedDigits();

        for (const pos of doubleCrossed) {
            this.drawCellBorder(pos, COLORS.red);
        }
    }

    /** Red border where a signal crashes into a non-receiver antenna part. */
    private drawCollisionWarnings(game: AntennasGame): void {
        for (const pos of game.getCollisionErrorCells()) {
            this.drawCellBorder(pos, COLORS.red);
        }
    }

    private drawSignalArrows(game: AntennasGame): void {
        for (const [, antenna] of game.getAntennas()) {
            const crossings = game.getSignalDigitCrossings(antenna);
            if (crossings.length === 0) continue;

            // Arrow color matches the signal's colour.
            const arrowColor = this.color(game.getSignalColor(antenna));

            for (const crossing of crossings) {
                this.drawBeamEdgeTriangle(crossing.position, crossing.direction, arrowColor, true);
                this.drawBeamEdgeTriangle(crossing.position, crossing.direction, arrowColor, false);
            }
        }
    }

    private drawBeamEdgeTriangle(
        cellPos: Position, direction: Direction, color: string, inbound: boolean
    ): void {
        const { padding, cellSize } = this.config;
        const center = this.getCellCenter(cellPos);

        let x: number, y: number;
        let angle: number;

        switch (direction) {
            case 'S':
                x = center.x;
                y = inbound
                    ? padding + cellPos.row * cellSize
                    : padding + (cellPos.row + 1) * cellSize;
                angle = 0;
                break;
            case 'N':
                x = center.x;
                y = inbound
                    ? padding + (cellPos.row + 1) * cellSize
                    : padding + cellPos.row * cellSize;
                angle = Math.PI;
                break;
            case 'E':
                x = inbound
                    ? padding + cellPos.col * cellSize
                    : padding + (cellPos.col + 1) * cellSize;
                y = center.y;
                angle = -Math.PI / 2;
                break;
            case 'W':
                x = inbound
                    ? padding + (cellPos.col + 1) * cellSize
                    : padding + cellPos.col * cellSize;
                y = center.y;
                angle = Math.PI / 2;
                break;
        }

        this.ctx.save();
        this.ctx.translate(x, y);
        this.ctx.rotate(angle);

        this.ctx.fillStyle = color;
        this.ctx.beginPath();
        if (inbound) {
            this.ctx.moveTo(-5, 1);
            this.ctx.lineTo(5, 1);
            this.ctx.lineTo(0, 9);
        } else {
            this.ctx.moveTo(-5, -9);
            this.ctx.lineTo(5, -9);
            this.ctx.lineTo(0, -1);
        }
        this.ctx.closePath();
        this.ctx.fill();

        this.ctx.restore();
    }

}
